import { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, List, Heart, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';
import useParentData from '@/hooks/useParentData';
import ChildSelector from './ChildSelector';
import CalendarView from './CalendarView';

const HealthView = () => {
  const { children, selectedChild, setSelectedChild, loading } = useParentData();
  const [viewMode, setViewMode] = useState('calendar');
  const [healthData, setHealthData] = useState([]);
  const [stats, setStats] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (selectedChild) {
      fetchHealth(selectedChild.id);
    }
  }, [selectedChild]);

  const fetchHealth = async (studentId) => {
    setLoadingData(true);
    try {
      const response = await apiClient.get(`/v1/parent/child/${studentId}/health`);
      setHealthData(response.data.health_records || []);
      setStats(response.data.stats || {});
    } catch (error) {
      toast.error('Failed to load health data');
    } finally {
      setLoadingData(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      healthy: 'bg-green-500',
      sick: 'bg-red-500',
      injured: 'bg-orange-500',
      unwell: 'bg-yellow-500'
    };
    const key = status ? String(status).toLowerCase().trim() : '';
    return colors[key] || 'bg-gray-500';
  };

  const getStatusBadge = (status) => {
    const badges = {
      healthy: 'bg-green-100 text-green-800',
      sick: 'bg-red-100 text-red-800',
      injured: 'bg-orange-100 text-orange-800',
      unwell: 'bg-yellow-100 text-yellow-800'
    };
    const key = status ? String(status).toLowerCase().trim() : '';
    return badges[key] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const key = status ? String(status).toLowerCase().trim() : '';
    if (key === 'healthy') return 'H';
    if (key === 'sick') return 'S';
    if (key === 'injured') return 'I';
    return 'U';
  };

  const renderCalendarCell = (dayData) => (
    <div className="flex flex-col items-center justify-center h-full">
      <div className={`w-8 h-8 rounded-full ${getStatusColor(dayData.status)} flex items-center justify-center`}>
        <span className="text-white text-xs font-bold">
          {getStatusIcon(dayData.status)}
        </span>
      </div>
      {dayData.follow_up_required && (
        <AlertCircle className="w-3 h-3 text-red-500 mt-1" />
      )}
    </div>
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading || loadingData) {
    return <div className="p-6 text-center text-gray-500">Loading health records...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>Health Updates</h1>
        <p className="text-gray-600 mt-1">Monitor your child's health records</p>
      </div>

      <ChildSelector 
        children={children}
        selectedChild={selectedChild}
        onSelectChild={setSelectedChild}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Health Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>
              {stats.percentage || 0}%
            </div>
            <p className="text-xs text-gray-500 mt-1">Days marked healthy</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Healthy Days</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {stats.healthy || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">Out of {stats.total || 0} records</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Total Records</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>
              {stats.total || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        <Button
          variant={viewMode === 'calendar' ? 'default' : 'outline'}
          onClick={() => setViewMode('calendar')}
          className={viewMode === 'calendar' ? 'btn-primary text-white' : ''}
        >
          <CalendarIcon className="w-4 h-4 mr-2" />
          Calendar View
        </Button>
        <Button
          variant={viewMode === 'list' ? 'default' : 'outline'}
          onClick={() => setViewMode('list')}
          className={viewMode === 'list' ? 'btn-primary text-white' : ''}
        >
          <List className="w-4 h-4 mr-2" />
          List View
        </Button>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <CalendarView
          data={healthData}
          renderCell={renderCalendarCell}
          onDateClick={(date, data) => {
            if (data) {
              toast.info(`${date}: ${data.status}${data.symptoms ? ` - ${data.symptoms}` : ''}`);
            }
          }}
        />
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          <CardHeader>
            <CardTitle>Health Records</CardTitle>
          </CardHeader>
          <CardContent>
            {healthData.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No health records found</p>
            ) : (
              <div className="space-y-3">
                {healthData.map((record, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <Heart className={`w-5 h-5 ${record.status === 'healthy' ? 'text-green-600' : 'text-red-600'}`} />
                        <div>
                          <p className="font-medium">{formatDate(record.date)}</p>
                          <div className={`inline-block px-2 py-1 rounded text-xs font-medium mt-1 ${getStatusBadge(record.status)}`}>
                            {record.status}
                          </div>
                        </div>
                      </div>
                      {record.follow_up_required && (
                        <div className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Follow-up Required
                        </div>
                      )}
                    </div>
                    {(record.symptoms || record.notes || record.description) && (
                      <div className="mt-2 text-sm">
                        <span className="text-gray-600">Symptoms: </span>
                        <span className="text-gray-800">{record.symptoms || record.notes || record.description}</span>
                      </div>
                    )}
                    {(record.treatment || record.visit_details) && (
                      <div className="mt-1 text-sm">
                        <span className="text-gray-600">Treatment: </span>
                        <span className="text-gray-800">{record.treatment || record.visit_details}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HealthView;
